import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { KeycloakAdminApi } from '../../services/keycloak-admin-api';

const CLIENT_ID_PATTERN = /^[a-zA-Z0-9_-]+$/;

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9_-]/g, '');
}

@Component({
  selector: 'app-request-client',
  imports: [RouterLink, ReactiveFormsModule],
  templateUrl: './request-client.html',
  styleUrl: './request-client.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestClient {
  private keycloakApi = inject(KeycloakAdminApi);

  isSubmitting = signal(false);
  submissionError = signal<string | null>(null);
  submissionSuccess = signal(false);
  // Tracks whether the user manually edited the clientId so we stop auto-deriving it from name
  private clientIdManuallyEdited = false;

  form = new FormGroup({
    name: new FormControl('', [
      Validators.required,
      Validators.minLength(2),
      Validators.maxLength(50),
    ]),
    clientId: new FormControl('', [
      Validators.required,
      Validators.minLength(2),
      Validators.maxLength(50),
      Validators.pattern(CLIENT_ID_PATTERN),
    ]),
  });

  constructor() {
    this.form.get('name')?.valueChanges.subscribe((name) => {
      if (this.clientIdManuallyEdited) {
        return;
      }
      this.form.get('clientId')?.setValue(slugify(name || ''), { emitEvent: false });
    });
  }

  onClientIdInput(): void {
    this.clientIdManuallyEdited = true;
  }

  getFieldError(fieldName: string): string {
    const field = this.form.get(fieldName);
    if (field?.errors && field.touched) {
      if (field.errors['required']) return `${fieldName} is required`;
      if (field.errors['minlength'])
        return `Must be at least ${field.errors['minlength'].requiredLength} characters`;
      if (field.errors['maxlength'])
        return `Cannot exceed ${field.errors['maxlength'].requiredLength} characters`;
      if (field.errors['pattern'])
        return 'Can only contain letters, numbers, hyphens, and underscores';
    }
    return '';
  }

  async submit(): Promise<void> {
    this.submissionError.set(null);
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSubmitting.set(true);
    try {
      await this.keycloakApi.createClientRequest(
        this.form.get('name')?.value?.trim() || '',
        this.form.get('clientId')?.value?.trim() || ''
      );
      this.submissionSuccess.set(true);
    } catch (error) {
      this.submissionError.set(
        error instanceof Error ? error.message : 'Failed to submit client request'
      );
    } finally {
      this.isSubmitting.set(false);
    }
  }
}
